/* Smart Panels / Horizon: live Add-to-Cart total, scoped to product forms. */
(() => {
  if (window.__spAtcTotalInstalled) return;
  window.__spAtcTotalInstalled = true;

  const selector = '.buy-buttons-block product-form-component';
  const forms = () => document.querySelectorAll(selector);
  const fmt = (currency) => {
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' });
    } catch (_) {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
    }
  };

  function read(form) {
    const data = form.querySelector('[data-sp-atc-price-data]');
    const button = form.querySelector('add-to-cart-component button[name="add"]');
    const id = form.querySelector('form input[name="id"]');
    const qty = form.querySelector('quantity-selector-component input[name="quantity"]');
    if (!data || !button || !id || !qty) return null;
    return { data, button, id, qty };
  }

  function hideNativeButtonLabel(button) {
    // Horizon versions differ: some output native text directly, others wrap it.
    // Hide only native label content on THIS product-form button, not the icon,
    // checkmark animation, shared button snippet, or our own combined label.
    Array.from(button.childNodes)
      .filter((node) => node.nodeType === 3 && node.textContent.trim())
      .forEach((node) => node.remove());

    const nativeLabels = button.querySelectorAll(
      ':scope > .add-to-cart-text:not([data-sp-atc-ui]), ' +
      ':scope > .atc-button--text, ' +
      ':scope > .add-to-cart-button__text, ' +
      ':scope > .add-to-cart-label, ' +
      ':scope > [data-add-to-cart-text]'
    );
    nativeLabels.forEach((node) => {
      // Keep any native cart icon even if Horizon placed it inside the label.
      const icon = node.querySelector('.add-to-cart-icon, .svg-wrapper:not(.add-to-cart__added-icon)');
      if (icon && !icon.closest('.add-to-cart__added')) {
        button.insertBefore(icon, node);
      }
      node.hidden = true;
      node.setAttribute('aria-hidden', 'true');
    });
  }

  function install(button) {
    // Preserve the native submit handler, cart icon and checkmark animation.
    hideNativeButtonLabel(button);
    if (button.querySelector('[data-sp-atc-ui]')) return;
    const ui = document.createElement('span');
    // Do NOT add Horizon's .add-to-cart-text: it has native animation/clipping styles.
    ui.className = 'custom-atc-text';
    ui.setAttribute('data-sp-atc-ui', '');
    const label = document.createElement('span');
    label.setAttribute('data-sc-atc-label', '');
    const price = document.createElement('span');
    price.setAttribute('data-atc-total-price', '');
    price.setAttribute('aria-live', 'polite');
    ui.append(label, price);
    // Place the custom label before either Horizon version of the Added indicator.
    button.insertBefore(ui, button.querySelector('.add-to-cart__added, .add-to-cart-text--added'));
    button.setAttribute('data-sp-atc-total-ready', '');
  }

  function update(form) {
    const state = read(form);
    if (!state) return;
    const { data, button, id, qty } = state;
    install(button);
    const label = button.querySelector('[data-sc-atc-label]');
    const price = button.querySelector('[data-atc-total-price]');
    if (!label || !price) return;

    const variant = Array.from(data.querySelectorAll('[data-sp-atc-variant]'))
      .find((v) => v.getAttribute('data-sp-atc-variant') === id.value);
    const cents = variant ? Number(variant.getAttribute('data-sp-atc-price-cents')) : NaN;
    const available = variant?.getAttribute('data-sp-atc-available') === 'true';
    const quantity = Number(qty.value);
    const valid = available && !button.disabled && Number.isFinite(cents) && cents >= 0
      && Number.isSafeInteger(quantity) && quantity > 0;

    if (valid) {
      label.textContent = data.dataset.addLabel || 'Add to cart';
      price.textContent = fmt(data.dataset.currency).format(cents * quantity / 100);
      price.hidden = false;
    } else {
      label.textContent = variant && !available
        ? (data.dataset.soldOutLabel || 'Sold out')
        : (data.dataset.unavailableLabel || 'Unavailable');
      price.textContent = '';
      price.hidden = true;
    }
  }

  function initAll(root = document) {
    if (root.matches?.(selector)) update(root);
    root.querySelectorAll?.(selector).forEach(update);
  }

  // Native plus/minus buttons and manual quantity input.
  document.addEventListener('quantity-selector:update', (event) => {
    const form = event.target.closest?.(selector);
    if (form) update(form);
  });
  document.addEventListener('input', (event) => {
    if (!event.target.matches?.('quantity-selector-component input[name="quantity"]')) return;
    const form = event.target.closest(selector);
    if (form) update(form);
  });
  document.addEventListener('change', (event) => {
    if (!event.target.matches?.('quantity-selector-component input[name="quantity"], product-form-component input[name="id"]')) return;
    const form = event.target.closest(selector);
    if (form) update(form);
  });
  document.addEventListener('click', (event) => {
    if (!event.target.closest?.('quantity-selector-component button[name="plus"], quantity-selector-component button[name="minus"]')) return;
    const form = event.target.closest(selector);
    if (form) requestAnimationFrame(() => update(form));
  });

  // Horizon can asynchronously resolve a newly selected variant.
  document.addEventListener('shopify:product:select', (event) => {
    const container = event.target?.closest?.('.shopify-section, dialog, product-card');
    if (!container) return;
    const refresh = () => initAll(container);
    if (event.promise && typeof event.promise.then === 'function') {
      event.promise.then(() => {
        setTimeout(refresh, 0);
        setTimeout(refresh, 150);
      }).catch(() => {});
    } else {
      setTimeout(refresh, 0);
      setTimeout(refresh, 150);
    }
  });

  const start = () => {
    initAll();
    const observer = new MutationObserver((entries) => {
      // Reinitialise if Horizon swaps/morphs the product form, button, or price data.
      if (!entries.some((entry) => Array.from(entry.addedNodes).some((n) =>
        n.nodeType === 1 && (
          n.matches?.('product-form-component, add-to-cart-component, [data-sp-atc-price-data], .buy-buttons-block') ||
          n.querySelector?.('product-form-component, add-to-cart-component, [data-sp-atc-price-data]')
        )
      ))) return;
      initAll();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
